import React, { useState } from "react";
import { ImageLightbox } from "../ui/ImageLightbox";
import { SectionBackgroundIcons } from "../ui/BackgroundIcons";

interface OurTeamSectionProps {
  heading: string;
  summary: string;
  fullText: string;
  images: [string, string]; // Exactly 2 images
}

export function OurTeamSection({
  heading,
  summary,
  fullText,
  images,
}: OurTeamSectionProps) {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section className="bg-[#0a0a0a] py-12 relative">
      <SectionBackgroundIcons variant="team" />
      <div className="relative z-10 max-w-5xl mx-auto px-6 w-full text-center">
        <div className="flex flex-col items-center text-center gap-8">
          {/* Separator */}
          <div className="w-12 h-px bg-[#a79c68]/80 mx-auto mb-2" />
          {/* Heading */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
            <span className="text-white">Our </span>
            <span className="text-[#a79c68]">Team</span>
          </h2>

          {/* Two Team Images Side-by-Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
            {images.map((image, index) => (
              <div
                key={index}
                className="relative aspect-[4/3] rounded-xl overflow-hidden bg-black cursor-pointer transition-transform hover:scale-105"
                onClick={() => setLightboxImage(image)}
              >
                <img
                  src={image}
                  alt={`${heading} ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>

          {/* Expandable Text */}
          <div className="text-lg text-gray-300 leading-relaxed max-w-3xl">
            <p className="whitespace-pre-line">
              {isExpanded
                ? fullText
                : `${summary.substring(0, summary.indexOf("we're a group") + 15)}...`}
              {isExpanded ? "  " : " "}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-[#a79c68] hover:text-[#c4a962] font-semibold underline transition-colors"
                style={{ fontSize: "0.67em" }}
              >
                {isExpanded ? "Show Less" : "Read More"}
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      <ImageLightbox
        imageUrl={lightboxImage || ""}
        imageAlt={heading}
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
      />
    </section>
  );
}
